import { IcMandatory12 } from '@assets/icons/12x12';
import { IcInfo16 } from '@assets/icons/16x16';
import { Button, Center, Field as ChakraField, Fieldset, HStack, Icon, Input, Text, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import { InputNumber } from '@components/ui/input/InputNumber';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectHeightSuffix } from '@redux/settings/settings.selectors';
import { Field, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';

import { Platform } from '@/types/config/config.types';

type Props = {
  setShowMapForm: (value: boolean) => void;
};

export default function PlatformGeneralForm({ setShowMapForm }: Readonly<Props>) {
  const showHeightSuffix = useAppSelector(selectHeightSuffix);

  const { errors, touched, initialValues, values, setFieldValue } = useFormikContext<Partial<Platform>>();
  return (
    <CockpitFormLayout title="components.platforms.general.title">
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <VStack gap={5}>
          <ChakraField.Root variant="cockpit" required invalid={touched.name && !!errors.name}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.platforms.general.name" />
              <RequiredIndicator />
              {initialValues.name !== values.name && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} type="text" name="name" id="name" />
          </ChakraField.Root>
          <HStack gap={5} width="100%">
            <ChakraField.Root variant="cockpit" required invalid={touched.altitude && !!errors.altitude}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.platforms.general.altitude" values={{ showHeightSuffix }} />
                {initialValues.altitude !== values.altitude && <ModifiedTag />}
              </ChakraField.Label>
              <Field
                as={InputNumber}
                type="text"
                name="altitude"
                id="altitude"
                allowMouseWheel
                onChange={(value?: number) => setFieldValue('altitude', value)}
                suffix="m"
              />
            </ChakraField.Root>
            <ChakraField.Root variant="cockpit" required invalid={touched.heightOffset && !!errors.heightOffset}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.platforms.general.heightOffset" />
                <RequiredIndicator />
                <Tooltip
                  positioning={{ placement: 'top' }}
                  content={<FormattedMessage id="components.platforms.general.heightOffsetInfo" />}
                >
                  <Icon as={IcInfo16} color="neutral.500" />
                </Tooltip>
                {initialValues.heightOffset !== values.heightOffset && <ModifiedTag />}
              </ChakraField.Label>
              <Field
                as={InputNumber}
                type="text"
                name="heightOffset"
                id="heightOffset"
                allowMouseWheel
                onChange={(value?: number) => setFieldValue('heightOffset', value)}
                suffix="m"
              />
            </ChakraField.Root>
          </HStack>
          <VStack gap={2} width="100%" alignItems="start">
            <HStack>
              <Text color="neutral.300">
                <FormattedMessage id="components.platforms.general.perimeterDefinition" />
              </Text>
              <Icon as={IcMandatory12} boxSize="12px" />
            </HStack>

            <Center width="100%" padding={4} border="2px solid" borderColor="neutral.850">
              <Button variant="toggle" size="toggleM" flex={1} data-active onClick={() => setShowMapForm(true)}>
                <FormattedMessage id="components.platforms.general.draw" />
              </Button>
            </Center>
          </VStack>
        </VStack>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
